import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle, XCircle, Loader2, Trash2, RefreshCw, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [biz, setBiz] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/settings/integrations").then(r => setStatus(r.data));
    api.get("/settings/business").then(r => setBiz(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("google") === "connected") {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, "", "/admin/settings");
      load();
    }
  }, [load]);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/integrations/google/connect");
      if (data.authorization_url) window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Google not configured"); }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try { await api.post("/integrations/google/disconnect"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const saveBiz = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/business", biz);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearDemo = async () => {
    if (!window.confirm("Clear all demo/customer data? (Templates and users are preserved.)")) return;
    try { await api.post("/settings/clear-demo-data"); toast.success("Data cleared"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reseedDemo = async () => {
    try { await api.post("/settings/reseed-demo-data"); toast.success("Demo data reseeded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!status || !biz) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Settings</h1>
        <p className="text-slate-600 mt-1">Integrations, business hours, and data controls.</p>
      </div>

      {/* Integrations */}
      <div className="owc-card p-6 space-y-4">
        <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)]">Integrations</h2>
        <IntegrationRow
          name="Twilio SMS"
          desc="Outbound + inbound SMS with STOP compliance."
          connected={status.twilio.connected}
          detail={status.twilio.phone}
          missingHelp="Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER to backend/.env"
          badgeId={T.settings.twilioBadge}
        />
        <IntegrationRow
          name="SendGrid Email"
          desc="Transactional email with open/click tracking."
          connected={status.sendgrid.connected}
          detail={status.sendgrid.from_email}
          missingHelp="Add SENDGRID_API_KEY and SENDGRID_FROM_EMAIL to backend/.env"
          badgeId={T.settings.sendgridBadge}
        />
        <IntegrationRow
          name="Google Calendar"
          desc="2-way sync — jobs create/update/delete calendar events."
          connected={status.google_calendar.connected}
          detail={status.google_calendar.connected_email}
          missingHelp="Add GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, GOOGLE_REDIRECT_URI to backend/.env, then click Connect."
          badgeId={T.settings.googleBadge}
          action={status.google_calendar.connected ? (
            <button onClick={disconnectGoogle} className="owc-btn-outline text-xs inline-flex items-center gap-2"><Trash2 className="h-3.5 w-3.5" /> Disconnect</button>
          ) : (
            <button data-testid={T.settings.connectGoogle} onClick={connectGoogle} className="owc-btn-accent text-xs inline-flex items-center gap-2"><LinkIcon className="h-3.5 w-3.5" /> Connect Google</button>
          )}
        />
      </div>

      {/* Business settings */}
      <div className="owc-card p-6 space-y-4">
        <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)]">Business preferences</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Company phone</label>
            <input className="owc-input mt-1" value={biz.company_phone || ""} onChange={e => setBiz(b => ({ ...b, company_phone: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Company email</label>
            <input className="owc-input mt-1" value={biz.company_email || ""} onChange={e => setBiz(b => ({ ...b, company_email: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Service area ZIP center</label>
            <input className="owc-input mt-1" value={biz.service_area_center_zip || ""} onChange={e => setBiz(b => ({ ...b, service_area_center_zip: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Radius (miles)</label>
            <input type="number" className="owc-input mt-1" value={biz.service_area_radius_miles || 25} onChange={e => setBiz(b => ({ ...b, service_area_radius_miles: Number(e.target.value) }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Quiet hours start</label>
            <input type="time" className="owc-input mt-1" value={biz.quiet_hours?.start || "21:00"} onChange={e => setBiz(b => ({ ...b, quiet_hours: { ...b.quiet_hours, start: e.target.value } }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Quiet hours end</label>
            <input type="time" className="owc-input mt-1" value={biz.quiet_hours?.end || "08:00"} onChange={e => setBiz(b => ({ ...b, quiet_hours: { ...b.quiet_hours, end: e.target.value } }))} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 uppercase">Review URL (for post-job requests)</label>
            <input className="owc-input mt-1" value={biz.review_url || ""} onChange={e => setBiz(b => ({ ...b, review_url: e.target.value }))} />
          </div>
        </div>
        <button disabled={saving} onClick={saveBiz} className="owc-btn-accent text-sm inline-flex items-center gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Save preferences</button>
      </div>

      {/* Data tools */}
      <div className="owc-card p-6 space-y-3">
        <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)]">Data tools</h2>
        <p className="text-sm text-slate-600">Handy for launch prep. Templates, services, and user accounts are never touched.</p>
        <div className="flex gap-3 flex-wrap">
          <button data-testid={T.settings.clearDemo} onClick={clearDemo} className="owc-btn-outline text-sm inline-flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Clear demo data</button>
          <button data-testid={T.settings.reseedDemo} onClick={reseedDemo} className="owc-btn-outline text-sm inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Reseed demo data</button>
        </div>
      </div>
    </div>
  );
}

function IntegrationRow({ name, desc, connected, detail, missingHelp, badgeId, action }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
      <div>
        <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)] flex items-center gap-2">
          {name}
          <span data-testid={badgeId} className={connected ? "owc-badge-green" : "owc-badge-red"}>
            {connected ? <><CheckCircle className="h-3 w-3" /> Connected</> : <><XCircle className="h-3 w-3" /> Not connected</>}
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-1">{desc}</p>
        {connected && detail && <div className="text-xs text-slate-500 mt-1">{detail}</div>}
        {!connected && <div className="text-xs text-slate-500 mt-1 italic">{missingHelp}</div>}
      </div>
      {action}
    </div>
  );
}
